import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import api, { getAllRecords, openProtectedFile } from "../api";
import TablePagination, { useTablePagination } from "../components/TablePagination";

const today = new Date().toISOString().slice(0, 10);
const empty = {
  fullName: "",
  mobileNo: "",
  email: "",
  dob: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  driverType: "Permanent",
  joiningDate: "",
  licenseNumber: "",
  licenseType: "LMV",
  licenseIssueDate: "",
  licenseExpiryDate: "",
  aadhaarNumber: "",
  panNumber: "",
  bankHolderName: "",
  accountNumber: "",
  ifscCode: "",
  status: "Active",
};
const fields = [
  ["aadhaarCard", "Aadhaar Card"],
  ["panCard", "PAN Card (Optional)"],
  ["drivingLicense", "Driving License"],
  ["bankDocument", "Bank Passbook / Cancelled Cheque (Optional)"],
  ["photo", "Driver Photo"],
];

function Field({ label, name, register, errors, required, ...props }) {
  const optional = [
    "email",
    "panNumber",
    "bankHolderName",
    "accountNumber",
    "ifscCode",
  ].includes(name);
  const isRequired = required || name === "aadhaarNumber";
  const validation = {
    ...(isRequired ? { required: `${label} is required.` } : {}),
  };
  if (name === "fullName") {
    validation.validate = (value) =>
      value.trim().length > 0 || "Driver full name is required.";
  }
  if (name === "mobileNo") {
    validation.pattern = {
      value: /^\d{10}$/,
      message: "Enter a valid 10-digit mobile number.",
    };
  }
  if (name === "aadhaarNumber") {
    validation.pattern = {
      value: /^\d{12}$/,
      message: "Enter a valid 12-digit Aadhaar number.",
    };
  }
  if (name === "panNumber") {
    validation.pattern = {
      value: /^[A-Za-z]{5}\d{4}[A-Za-z]$/,
      message: "Use PAN format ABCDE1234F.",
    };
  }
  if (name === "email") {
    validation.pattern = {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Enter a valid email address.",
    };
  }
  if (name === "licenseExpiryDate") {
    validation.validate = (value) =>
      value >= today || "License expiry date cannot be in the past.";
  }
  return (
    <label>
      {label}
      {name === "aadhaarNumber" && " *"}
      {optional && " (Optional)"}
      <input
        {...props}
        {...register(name, validation)}
      />
      {errors[name] && <small>{errors[name].message}</small>}
    </label>
  );
}

function DriverForm({ driver, close, saved }) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: driver
      ? {
          ...empty,
          ...driver,
          dob: driver.dob?.slice(0, 10),
          joiningDate: driver.joiningDate?.slice(0, 10),
          licenseIssueDate: driver.licenseIssueDate?.slice(0, 10),
          licenseExpiryDate: driver.licenseExpiryDate?.slice(0, 10),
        }
      : empty,
  });
  const [error, setError] = useState("");
  useEffect(() => {
    reset(
      driver
        ? {
            ...empty,
            ...driver,
            dob: driver.dob?.slice(0, 10),
            joiningDate: driver.joiningDate?.slice(0, 10),
            licenseIssueDate: driver.licenseIssueDate?.slice(0, 10),
            licenseExpiryDate: driver.licenseExpiryDate?.slice(0, 10),
          }
        : empty,
    );
  }, [driver, reset]);
  const submit = async (values) => {
    setError("");
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (value instanceof FileList) {
          if (value[0]) formData.append(key, value[0]);
        } else if (value !== undefined && value !== null)
          formData.append(key, value);
      });
      await (driver
        ? api.put(`/drivers/${driver._id}`, formData)
        : api.post("/drivers", formData));
      saved(
        driver
          ? "Driver updated successfully."
          : "Driver registered successfully.",
      );
      close();
    } catch (err) {
      setError(err.response?.data?.message || "Unable to save driver.");
    }
  };
  return (
    <div className="driver-modal-backdrop">
      <form
        className="driver-modal"
        noValidate
        onSubmit={handleSubmit(submit)}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <p>{driver ? "EDIT DRIVER" : "NEW DRIVER"}</p>
            <h3>{driver ? "Update Driver" : "Driver Registration"}</h3>
          </div>
          <button type="button" onClick={close}>
            ×
          </button>
        </header>
        <div className="driver-form-body">
          <section>
            <h4>Personal Information</h4>
            <div className="driver-form-grid">
              <Field
                label="Driver Full Name"
                name="fullName"
                register={register}
                errors={errors}
                required
              />
              <Field
                label="Mobile Number"
                name="mobileNo"
                register={register}
                errors={errors}
                required
                inputMode="numeric"
                maxLength="10"
                pattern="\d{10}"
                title="Enter a 10 digit mobile number"
                onInput={(event) => {
                  event.currentTarget.value = event.currentTarget.value.replace(/\D/g, "");
                }}
              />
              <Field
                label="Email ID"
                name="email"
                type="email"
                register={register}
                errors={errors}
              />
              <Field
                label="Date of Birth"
                name="dob"
                type="date"
                register={register}
                errors={errors}
              />
              <Field
                label="Address"
                name="address"
                register={register}
                errors={errors}
              />
              <Field
                label="City"
                name="city"
                register={register}
                errors={errors}
              />
              <Field
                label="State"
                name="state"
                register={register}
                errors={errors}
              />
              <Field
                label="Pincode"
                name="pincode"
                register={register}
                errors={errors}
              />
            </div>
          </section>
          <section>
            <h4>Employment Information</h4>
            <div className="driver-form-grid">
              <label>
                Driver Type
                <select
                  {...register("driverType", {
                    required: "Driver type is required.",
                  })}
                >
                  <option>Permanent</option>
                  <option>Adhoc</option>
                </select>
              </label>
              <Field
                label="Joining Date"
                name="joiningDate"
                type="date"
                register={register}
                errors={errors}
              />
              <label>
                Status
                <select {...register("status")}>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </label>
            </div>
          </section>
          <section>
            <h4>License Information</h4>
            <div className="driver-form-grid">
              <Field
                label="Driving License Number"
                name="licenseNumber"
                register={register}
                errors={errors}
                required
              />
              <label>
                License Type
                <select
                  {...register("licenseType", {
                    required: "License type is required.",
                  })}
                >
                  <option>LMV</option>
                  <option>HMV</option>
                  <option>Transport</option>
                </select>
              </label>
              <Field
                label="License Issue Date"
                name="licenseIssueDate"
                type="date"
                register={register}
                errors={errors}
              />
              <Field
                label="License Expiry Date"
                name="licenseExpiryDate"
                type="date"
                min={today}
                register={register}
                errors={errors}
                required
              />
            </div>
          </section>
          <section>
            <h4>Identity & Bank Details</h4>
            <div className="driver-form-grid">
              <Field
                label="Aadhaar Number"
                name="aadhaarNumber"
                register={register}
                errors={errors}
                inputMode="numeric"
                maxLength="12"
                pattern="\d{12}"
                title="Enter a 12 digit Aadhaar number"
                onInput={(event) => {
                  event.currentTarget.value = event.currentTarget.value.replace(/\D/g, "");
                }}
              />
              <Field
                label="PAN Number"
                name="panNumber"
                register={register}
                errors={errors}
                pattern="[A-Za-z]{5}[0-9]{4}[A-Za-z]"
                title="Use ABCDE1234F format"
              />
              <Field
                label="Account Holder Name"
                name="bankHolderName"
                register={register}
                errors={errors}
              />
              <Field
                label="Account Number"
                name="accountNumber"
                register={register}
                errors={errors}
              />
              <Field
                label="IFSC Code"
                name="ifscCode"
                register={register}
                errors={errors}
              />
            </div>
          </section>
          <section>
            <h4>Document Uploads</h4>
            <p className="driver-help">
              Upload PDF, JPG, or PNG files (maximum 5 MB each).
            </p>
            <div className="driver-form-grid">
              {fields.map(([name, label]) => (
                <label key={name}>
                  {label}
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    {...register(name)}
                  />
                </label>
              ))}
            </div>
          </section>
          {error && <p className="driver-error">{error}</p>}
        </div>
        <footer>
          <button type="button" className="cancel" onClick={close}>
            Cancel
          </button>
          <button disabled={isSubmitting}>
            {isSubmitting
              ? "Saving…"
              : driver
                ? "Update Driver"
                : "Register Driver"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function DriverDetails({ driver, close }) {
  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString("en-IN") : "—";
  const details = [
    ["Driver ID", driver.driverId],
    ["Mobile", driver.mobileNo],
    ["Email", driver.email],
    ["Date of birth", formatDate(driver.dob)],
    [
      "Address",
      [driver.address, driver.city, driver.state, driver.pincode]
        .filter(Boolean)
        .join(", "),
    ],
    ["Driver type", driver.driverType],
    ["Joining date", formatDate(driver.joiningDate)],
    ["Status", driver.status],
    ["Licence number", driver.licenseNumber],
    ["Licence type", driver.licenseType],
    ["Licence issue date", formatDate(driver.licenseIssueDate)],
    ["Licence expiry date", formatDate(driver.licenseExpiryDate)],
    ["Aadhaar number", driver.aadhaarNumber],
    ["PAN number", driver.panNumber],
    ["Bank account holder", driver.bankHolderName],
    ["Account number", driver.accountNumber],
    ["IFSC code", driver.ifscCode],
  ];
  return (
    <div className="driver-modal-backdrop">
      <div
        className="driver-modal driver-view"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <p>DRIVER DETAILS</p>
            <h3>{driver.fullName}</h3>
          </div>
          <button onClick={close}>×</button>
        </header>
        <div className="driver-form-body">
          <section>
            <h4>Driver profile</h4>
            <div className="driver-form-grid">
              {details.map(([label, value]) => (
                <p key={label}>
                  <b>{label}</b>
                  <span>{value || "—"}</span>
                </p>
              ))}
            </div>
          </section>
          <section>
            <h4>Uploaded documents</h4>
            <div className="driver-form-grid">
              {fields.map(([key, label]) => (
                <p key={key}>
                  <b>{label}</b>
                  {driver.documents?.[key] ? (
                    <button
                      type="button"
                      onClick={() =>
                        openProtectedFile(driver.documents[key]).catch(() =>
                          window.alert("Unable to open this document."),
                        )
                      }
                    >
                      View uploaded document
                    </button>
                  ) : (
                    <span>Not uploaded</span>
                  )}
                </p>
              ))}
            </div>
          </section>
        </div>
        <footer>
          <button onClick={() => window.print()}>Print Driver Details</button>
        </footer>
      </div>
    </div>
  );
}

export default function DriverManagementPage() {
  const [drivers, setDrivers] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    expired: 0,
    contract: 0,
  });
  const [filters, setFilters] = useState({
    query: "",
    driverType: "",
    status: "",
  });
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState(null);
  const [notice, setNotice] = useState("");
  const load = useCallback(async () => {
    try {
        const [driverRows, overview] = await Promise.all([
          getAllRecords("/drivers", filters),
          api.get("/drivers/summary"),
        ]);
        setDrivers(driverRows);
      setSummary(overview.data);
    } catch (err) {
      setNotice(err.response?.data?.message || "Unable to load drivers.");
    }
  }, [filters]);
  useEffect(() => {
    load();
  }, [load]);
  const { page, setPage, pageRows } = useTablePagination(drivers);
  const exportCsv = () => {
    const headings = [
      "Driver ID",
      "Name",
      "Mobile",
      "License",
      "Type",
      "Status",
    ];
    const csv = [
      headings,
      ...drivers.map((d) => [
        d.driverId,
        d.fullName,
        d.mobileNo,
        d.licenseNumber,
        d.driverType,
        d.status,
      ]),
    ]
      .map((row) =>
        row
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    link.download = "drivers.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };
  const updateFilter = (key) => (event) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: event.target.value }));
  };
  return (
    <div className="driver-management">
      <div className="driver-heading">
        <div>
          <p>FLEET MANAGEMENT</p>
          <h2>Driver Management</h2>
          <span>Register and manage your company drivers.</span>
        </div>
        <button
          onClick={() => {
            setSelected(null);
            setShowForm(true);
          }}
        >
          + Add Driver
        </button>
      </div>
      {notice && (
        <div className="driver-notice">
          {notice}
          <button onClick={() => setNotice("")}>×</button>
        </div>
      )}
      <div className="driver-summary">
        {[
          ["Total Drivers", summary.total],
          ["Active Drivers", summary.active],
          ["Expired Licenses", summary.expired],
          ["Contract Drivers", summary.contract],
        ].map(([label, value]) => (
          <article key={label}>
            <small>{label}</small>
            <b>{value}</b>
          </article>
        ))}
      </div>
      <section className="driver-list">
        <div className="driver-tools">
          <input
            placeholder="Search driver name or mobile"
            value={filters.query}
            onChange={updateFilter("query")}
          />
          <select
            value={filters.driverType}
            onChange={updateFilter("driverType")}
          >
            <option value="">All driver types</option>
            <option>Permanent</option>
            <option>Adhoc</option>
          </select>
          <select value={filters.status} onChange={updateFilter("status")}>
            <option value="">All statuses</option>
            <option>Active</option>
            <option>Inactive</option>
          </select>
          <button onClick={exportCsv}>Export Excel</button>
          <button onClick={() => window.print()}>Print / PDF</button>
        </div>
        <div className="driver-table">
          <table>
            <thead>
              <tr>
                {[
                  "Driver ID",
                  "Driver Name",
                  "Mobile",
                  "License",
                  "Type",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((driver) => (
                <tr key={driver._id}>
                  <td>{driver.driverId}</td>
                  <td>{driver.fullName}</td>
                  <td>{driver.mobileNo}</td>
                  <td>{driver.licenseNumber}</td>
                  <td>{driver.driverType}</td>
                  <td>
                    <span
                      className={`driver-status ${driver.status.toLowerCase()}`}
                    >
                      {driver.status}
                    </span>
                  </td>
                  <td>
                    <button onClick={() => setView(driver)}>View</button>
                    <button
                      onClick={() => {
                        setSelected(driver);
                        setShowForm(true);
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {!drivers.length && (
                <tr>
                  <td colSpan="7">No drivers found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination page={page} setPage={setPage} total={drivers.length} label="Drivers" />
      </section>
      {showForm && (
        <DriverForm
          driver={selected}
          close={() => setShowForm(false)}
          saved={(message) => {
            setNotice(message);
            load();
          }}
        />
      )}
      {view && <DriverDetails driver={view} close={() => setView(null)} />}
    </div>
  );
}
